/**
 * How a work order moves, in plain words — the one description both the
 * list and each order's own page show, so the process reads the same
 * everywhere it appears.
 */
import type { WorkOrderStatus } from "./workOrders";

export interface FlowStep {
  status: WorkOrderStatus;
  /** what this step means, in one short line */
  means: string;
  /** who moves an order into this step */
  who: string;
}

/** The main path, in order. */
export const FLOW: FlowStep[] = [
  { status: "draft", means: "Reported. Waiting for approval.", who: "Anyone who can create work orders" },
  { status: "approved", means: "Approved. Waiting for a crew.", who: "Supervisor or Administrator" },
  { status: "dispatched", means: "Assigned to a crew.", who: "Supervisor or Administrator" },
  { status: "in_progress", means: "The crew is working on it.", who: "Technician, from the phone app" },
  { status: "completed", means: "Work done. Waiting for a check.", who: "Technician" },
  { status: "verified", means: "Checked and closed.", who: "Supervisor or Accountable Manager" },
];

/** The two states off the main path. */
export const SIDE: FlowStep[] = [
  { status: "on_hold", means: "Paused for parts, access or weather.", who: "Supervisor or Technician" },
  { status: "cancelled", means: "Stopped. Kept in the record.", who: "Supervisor or Administrator" },
];

/** What happens next to an order, in a sentence — or null once it is finished. */
export function nextStep(status: WorkOrderStatus): FlowStep | null {
  if (status === "on_hold") return FLOW.find((s) => s.status === "in_progress") ?? null;
  const i = FLOW.findIndex((s) => s.status === status);
  return i >= 0 && i < FLOW.length - 1 ? FLOW[i + 1] : null;
}
